import { useEffect, useState } from "react";
import axios from "../../api/axios";
import SidebarClient from "../../components/SidebarClient";

export default function BulkMessage() {
  const [numbers, setNumbers] = useState("");
  const [message, setMessage] = useState("");
  const [delayMs, setDelayMs] = useState("800");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [isUrgent, setIsUrgent] = useState(false);

  // Auto-Save Tag Integrator
  const [tagInput, setTagInput] = useState("");
  const [loadingTag, setLoadingTag] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTagsList, setLoadingTagsList] = useState(true);

  const fetchTags = async () => {
    try {
      const res = await axios.get("/contacts/unique-tags");
      const tags = res.data?.tags || [];
      setAvailableTags(tags);
      if (tags.length > 0) {
        setTagInput(tags[0]);
      }
    } catch (err) {
      console.error(err);
    }
    setLoadingTagsList(false);
  };

  // load default delay from client settings
  const loadDefaults = async () => {
    try {
      const res = await axios.get("/client-settings");
      if (typeof res.data?.bulk_delay_ms === "number") {
        setDelayMs(String(res.data.bulk_delay_ms));
      }
    } catch (_) { }
  };

  const handleSend = async () => {
    if (!numbers.trim()) {
      alert("Numbers are required");
      return;
    }

    setSending(true);
    setResult(null);
    try {
      if (file) {
        const form = new FormData();
        form.append("numbers", numbers);
        form.append("message", message || "");
        form.append("delayMs", String(Number(delayMs || 0)));
        form.append("isUrgent", String(isUrgent));
        form.append("file", file);

        const res = await axios.post("/whatsapp/bulk-media", form);
        setResult(res.data);
      } else {
        if (!message.trim()) {
          alert("Message is required for text bulk");
          setSending(false);
          return;
        }

        const res = await axios.post("/whatsapp/bulk", {
          numbers,
          message,
          delayMs: Number(delayMs || 0),
          isUrgent,
        });
        setResult(res.data);
      }
    } catch (err) {
      alert(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Bulk send failed");
    }
    setSending(false);
  };

  const handleLoadTag = async () => {
    if (!tagInput.trim()) {
      alert("Please enter a tag to load!");
      return;
    }
    setLoadingTag(true);
    try {
      const res = await axios.get(`/contacts?tags=${encodeURIComponent(tagInput.trim())}`);
      const list = Array.isArray(res.data) ? res.data : res.data?.contacts || [];
      if (list.length === 0) {
        alert(`No contacts found with the tag: ${tagInput}`);
      } else {
        // Extract all phone numbers and join them with newlines
        const newNumbers = list.map(c => c.phone_number || c.phone).filter(Boolean).join("\n");
        setNumbers(prev => (prev.trim() ? prev + "\n" + newNumbers : newNumbers));
        alert(`Successfully loaded ${list.length} numbers!`);
      }
    } catch (err) {
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to load contacts by tag");
    }
    setLoadingTag(false);
  };

  useEffect(() => {
    loadDefaults();
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarClient />
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6">Bulk Message</h1>

        <div className="bg-white p-4 md:p-8 rounded-2xl shadow-sm border border-gray-100 max-w-3xl">
          <div className="space-y-4">
            {/* Auto-Save Tag Integrator Bridge */}
            <div className="bg-blue-50 p-4 border border-blue-100 rounded-xl mb-6">
              <label className="text-xs font-bold text-blue-800 ml-1">IMPORT NUMBERS BY TAG</label>
              <div className="flex gap-2 mt-2">
                {loadingTagsList ? (
                  <select disabled className="flex-1 border border-blue-200 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-100 text-gray-500">
                    <option>Loading tags...</option>
                  </select>
                ) : availableTags.length === 0 ? (
                  <select disabled className="flex-1 border border-blue-200 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-gray-100 text-gray-500">
                    <option>No tags found</option>
                  </select>
                ) : (
                  <select
                    className="flex-1 border border-blue-200 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer bg-white"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                  >
                    {availableTags.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
                <button
                  onClick={handleLoadTag}
                  disabled={loadingTag}
                  className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {loadingTag ? "Loading..." : "Load Contacts"}
                </button>
              </div>
              <p className="text-xs text-blue-600 mt-2 ml-1">
                Tip: Enter <b>TOPUP_BCAST</b> to instantly load all numbers that received automated Top-Ups!
              </p>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 ml-1">NUMBERS</label>
              <textarea
                className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                rows={6}
                placeholder="One number per line (or comma separated)\n9198xxxxxxx\n9197xxxxxxx"
                value={numbers}
                onChange={(e) => setNumbers(e.target.value)}
              />
            </div>

            {/* URGENT BROADCAST TOGGLE */}
            <div className={`p-4 rounded-xl border transition cursor-pointer flex items-start gap-3 ${isUrgent ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200 hover:border-orange-200'}`} onClick={() => setIsUrgent(!isUrgent)}>
              <div className="mt-0.5">
                <input
                  type="checkbox"
                  checked={isUrgent}
                  onChange={(e) => setIsUrgent(e.target.checked)}
                  className="w-5 h-5 text-orange-500 rounded border-gray-300 focus:ring-orange-500 cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div>
                <label className="text-sm font-bold text-gray-900 cursor-pointer">⚡ Urgent Broadcast Mode</label>
                <p className={`text-xs mt-1 ${isUrgent ? 'text-orange-700 font-medium' : 'text-gray-500'}`}>
                  {isUrgent ? "WARNING: Artificial typing delays are disabled. Messages will blast at maximum speed. Use only for critical service alerts." : "Enable to skip anti-ban typing simulated delays and send alerts instantly."}
                </p>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 ml-1">MESSAGE / CAPTION</label>
              <textarea
                className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                rows={4}
                placeholder="Type your message (or caption if sending a file)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 ml-1">FILE (optional: Photo/PDF)</label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-gray-400 mt-1 ml-1">If you choose a file, the same file will be sent to all numbers.</p>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 ml-1">DELAY (ms)</label>
              <input
                type="number"
                className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                value={delayMs}
                onChange={(e) => setDelayMs(e.target.value)}
              />
              <p className="text-xs text-gray-400 mt-1 ml-1">Tip: use 800–1500ms to reduce blocks.</p>
            </div>

            <button
              onClick={handleSend}
              disabled={sending}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
            >
              {sending ? "Sending..." : "Send Bulk"}
            </button>
          </div>
        </div>

        {result && (
          <div className="mt-6 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 max-w-3xl">
            <h2 className="text-lg font-bold text-gray-800 mb-2">Result</h2>
            <p className="text-sm text-gray-600">
              Total: <b>{result.total}</b> | Success: <b className="text-green-600">{result.success}</b> | Failed: <b className="text-red-600">{result.failed}</b>
            </p>
            <div className="mt-4 max-h-64 overflow-y-auto border rounded-xl">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left">To</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {result.results.map((r, idx) => (
                    <tr key={idx}>
                      <td className="p-3 font-mono">{r.to}</td>
                      <td className="p-3">
                        {r.success ? (
                          <span className="text-green-700 font-bold">SENT</span>
                        ) : (
                          <span className="text-red-700 font-bold">FAILED</span>
                        )}
                      </td>
                      <td className="p-3 text-gray-600">{r.error || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
